import React, { useContext, useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import clsx from "clsx";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
} from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import PersonIcon from "@mui/icons-material/Person";

import PermissaoAcesso from "../../../components/PermissaoAcesso";
import PermissaoPapel from "../../../components/PermissaoPapel";
import { Context as PermissoesContext } from "../../../Context/PermissoesContext";
import useUser from "../../../hooks/useUser";
import { api } from "../../../services/Api";
import { compararDadosUsuario, CustomDialog } from "./util";
import VisaoGeralPermissoes from "./VisaoGeralPermissoes";
import "./styles.css";

const dadosUsuarioVazios = {
  originais: {
    acoes: {
      autorizadas: [],
      nao_autorizadas: [],
    },
    papel: null,
  },
  modificados: {
    acoes: {
      autorizadas: [],
      nao_autorizadas: [],
    },
    papel: null,
  },
};

function EstadoVazio({ children }) {
  return <p className="painel-empty-state">{children}</p>;
}

export default function PainelControle({
  usuarioSelecionado,
  setUsuarioSelecionado,
  setLoading,
  loading,
  onDirtyChange,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const username = useUser();
  const {
    sobreescreverPermissoes,
    setReload,
    checarPermissaoComAcao,
    checarPermissao,
  } = useContext(PermissoesContext);

  const [modulos, setModulos] = useState([]);
  const [dadosUsuario, setDadosUsuario] = useState(dadosUsuarioVazios);
  const [preMudanca, setPreMudanca] = useState({
    autorizadas: [],
    nao_autorizadas: [],
  });
  const [moduloSelecionado, setModuloSelecionado] = useState("");
  const [permissoesPapelSelecionado, setPermissoesPapelSelecionado] = useState([]);
  const [papeisModuloSelecionado, setPapeisModuloSelecionado] = useState([]);
  const [modo, setModo] = useState("permissoes");
  const [modoVisualizacao, setModoVisualizacao] = useState("geral");
  const [existemAlteracoesMacro, setExistemAlteracoesMacro] = useState(false);
  const [dialog, setDialog] = useState({
    open: false,
    title: null,
    text: null,
    continuar: null,
    voltar: null,
  });
  const [permissoesUsuario, setPermissoesUsuario] = useState([]);
  const [codModulo, setCodModulo] = useState("");

  const existemAlteracoesDetalhadas = useMemo(
    () => !compararDadosUsuario(dadosUsuario, modo),
    [dadosUsuario, modo]
  );

  const existemAlteracoesAtivas =
    modoVisualizacao === "geral" ? existemAlteracoesMacro : existemAlteracoesDetalhadas;

  useEffect(() => {
    onDirtyChange(existemAlteracoesAtivas);
  }, [existemAlteracoesAtivas, onDirtyChange]);

  useEffect(() => {
    async function carregarModulos() {
      setLoading(true);
      try {
        const { data } = await api.get("/v2/controleAcesso/modulos");
        setModulos(data.modulos || []);
      } catch (e) {
        enqueueSnackbar("Algo deu errado. Por favor, recarregue a página.", {
          variant: "error",
          autoHideDuration: 3000,
        });
      } finally {
        setLoading(false);
      }
    }

    carregarModulos();
  }, [enqueueSnackbar, setLoading]);

  useEffect(() => {
    async function carregarPermissoesUsuario() {
      setLoading(true);
      try {
        const {
          data: { permissoes },
        } = await api.get(`/v2/controleAcesso/usuario/${usuarioSelecionado.id}/permissoes`);
        setPermissoesUsuario(permissoes || []);
      } catch (e) {
        enqueueSnackbar("Não foi possível carregar as permissões do usuário.", {
          variant: "error",
          autoHideDuration: 3000,
        });
        setPermissoesUsuario([]);
      } finally {
        setLoading(false);
      }
    }

    carregarPermissoesUsuario();
  }, [enqueueSnackbar, setLoading, usuarioSelecionado.id]);

  useEffect(() => {
    setPermissoesPapelSelecionado([]);
  }, [modo, moduloSelecionado]);

  useEffect(() => {
    async function carregarDadosModulo() {
      setDadosUsuario(prevState => ({
        ...prevState,
        modificados: {
          ...prevState.originais,
        },
      }));
      setPreMudanca({ autorizadas: [], nao_autorizadas: [] });

      if (!moduloSelecionado) {
        setDadosUsuario(dadosUsuarioVazios);
        setCodModulo("");
        return;
      }

      const modulo = modulos.find(item => item.nome === moduloSelecionado);
      if (!modulo) return;

      setLoading(true);
      try {
        if (modo === "permissoes") {
          const { data } = await api.get(
            `/v2/controleAcesso/modulo/${modulo.id}/permissoes`
          );
          const permissoesModulo = data.permissoes || [];
          const acoesAutorizadas = permissoesModulo.filter(permissao =>
            permissoesUsuario.some(permissaoUsuario => permissaoUsuario.id === permissao.id)
          );
          const acoesNaoAutorizadas = permissoesModulo.filter(
            permissao =>
              !acoesAutorizadas.some(
                permissaoAutorizada => permissaoAutorizada.id === permissao.id
              )
          );

          setCodModulo(permissoesModulo[0]?.codigo?.replace("1", "0") || "");
          setDadosUsuario({
            originais: {
              acoes: {
                autorizadas: acoesAutorizadas,
                nao_autorizadas: acoesNaoAutorizadas,
              },
              papel: usuarioSelecionado.id_papel,
            },
            modificados: {
              acoes: {
                autorizadas: acoesAutorizadas,
                nao_autorizadas: acoesNaoAutorizadas,
              },
              papel: usuarioSelecionado.id_papel,
            },
          });
        } else {
          const [{ data: papeisModuloData }, { data: papeisUniversaisData }] =
            await Promise.all([
              api.get(`/v2/controleAcesso/modulo/${modulo.id}/papeis`),
              api.get("/v2/controleAcesso/papeis/universais"),
            ]);

          setPapeisModuloSelecionado([
            ...(papeisUniversaisData.papeis || []),
            ...(papeisModuloData.papeis || []),
          ]);
        }
      } catch (e) {
        enqueueSnackbar("Algo deu errado. Tente novamente.", {
          variant: "error",
          autoHideDuration: 3000,
        });
      } finally {
        setLoading(false);
      }
    }

    carregarDadosModulo();
  }, [
    enqueueSnackbar,
    modo,
    moduloSelecionado,
    modulos,
    permissoesUsuario,
    setLoading,
    usuarioSelecionado.id_papel,
  ]);

  useEffect(() => {
    async function carregarPermissoesPapel() {
      if (dadosUsuario.modificados.papel === null || modo !== "cargos" || !moduloSelecionado) {
        return;
      }

      const modulo = modulos.find(item => item.nome === moduloSelecionado);
      if (!modulo) return;

      setLoading(true);
      try {
        const {
          data: { permissoes },
        } = await api.get(
          `/v2/controleAcesso/papel/${dadosUsuario.modificados.papel}/permissoes`
        );

        if (permissoes?.[0]?.id_modulo === modulo.id) {
          setPermissoesPapelSelecionado(permissoes);
        } else {
          setPermissoesPapelSelecionado([]);
        }
      } catch (e) {
        enqueueSnackbar("Algo deu errado. Por favor, tente novamente.", {
          variant: "error",
          autoHideDuration: 3000,
        });
      } finally {
        setLoading(false);
      }
    }

    carregarPermissoesPapel();
  }, [
    dadosUsuario.modificados.papel,
    enqueueSnackbar,
    modo,
    moduloSelecionado,
    modulos,
    setLoading,
  ]);

  function checaUsuarioPermissao() {
    if (!checarPermissao("CA002")) {
      if (!codModulo || !checarPermissao(codModulo)) {
        enqueueSnackbar(`Você não tem permissão para realizar esta ação. Código: ${codModulo}`, {
          variant: "error",
          autoHideDuration: 3000,
        });
        return false;
      }
    }
    return true;
  }

  function mostrarDialogAlteracoesNaoSalvas(continuar) {
    setDialog({
      open: true,
      continuar: () => {
        continuar();
        setDialog(prevState => ({ ...prevState, open: false }));
      },
      voltar: () => setDialog(prevState => ({ ...prevState, open: false })),
      text: "Volte e salve as alterações ou continue sem salvá-las.",
      title: "Existem alterações não salvas",
    });
  }

  function removerPreMudanca(lista, acao) {
    return lista.filter(item => item.id !== acao.id);
  }

  function moverParaEsquerda() {
    if (!checaUsuarioPermissao()) return;

    if (preMudanca.nao_autorizadas.length === 0) {
      enqueueSnackbar("Você não selecionou nenhuma permissão.", {
        variant: "warning",
        autoHideDuration: 1000,
      });
      return;
    }

    setDadosUsuario(prevState => ({
      ...prevState,
      modificados: {
        ...prevState.modificados,
        acoes: {
          autorizadas: prevState.modificados.acoes.autorizadas.filter(
            acao => !preMudanca.nao_autorizadas.some(item => item.id === acao.id)
          ),
          nao_autorizadas: [
            ...prevState.modificados.acoes.nao_autorizadas,
            ...preMudanca.nao_autorizadas,
          ],
        },
      },
    }));
    setPreMudanca(prevState => ({ ...prevState, nao_autorizadas: [] }));
  }

  function moverParaDireita() {
    if (!checaUsuarioPermissao()) return;

    if (preMudanca.autorizadas.length === 0) {
      enqueueSnackbar("Você não selecionou nenhuma permissão.", {
        variant: "warning",
        autoHideDuration: 1000,
      });
      return;
    }

    setDadosUsuario(prevState => ({
      ...prevState,
      modificados: {
        ...prevState.modificados,
        acoes: {
          nao_autorizadas: prevState.modificados.acoes.nao_autorizadas.filter(
            acao => !preMudanca.autorizadas.some(item => item.id === acao.id)
          ),
          autorizadas: [
            ...prevState.modificados.acoes.autorizadas,
            ...preMudanca.autorizadas,
          ],
        },
      },
    }));
    setPreMudanca(prevState => ({ ...prevState, autorizadas: [] }));
  }

  function moverTodosParaEsquerda() {
    if (!checaUsuarioPermissao()) return;

    if (dadosUsuario.modificados.acoes.autorizadas.length === 0) {
      enqueueSnackbar("Não há ações autorizadas.", {
        variant: "warning",
        autoHideDuration: 1000,
      });
      return;
    }

    if (preMudanca.autorizadas.length !== 0 || preMudanca.nao_autorizadas.length !== 0) {
      enqueueSnackbar("Desselecione todas as ações antes.", {
        variant: "warning",
        autoHideDuration: 1000,
      });
      return;
    }

    setDadosUsuario(prevState => ({
      ...prevState,
      modificados: {
        ...prevState.modificados,
        acoes: {
          autorizadas: [],
          nao_autorizadas: [
            ...prevState.modificados.acoes.nao_autorizadas,
            ...prevState.modificados.acoes.autorizadas,
          ],
        },
      },
    }));
  }

  function moverTodosParaDireita() {
    if (!checaUsuarioPermissao()) return;

    if (dadosUsuario.modificados.acoes.nao_autorizadas.length === 0) {
      enqueueSnackbar("Não há ações não autorizadas.", {
        variant: "warning",
        autoHideDuration: 1000,
      });
      return;
    }

    if (preMudanca.autorizadas.length !== 0 || preMudanca.nao_autorizadas.length !== 0) {
      enqueueSnackbar("Desselecione todas as ações antes.", {
        variant: "warning",
        autoHideDuration: 1000,
      });
      return;
    }

    setDadosUsuario(prevState => ({
      ...prevState,
      modificados: {
        ...prevState.modificados,
        acoes: {
          nao_autorizadas: [],
          autorizadas: [
            ...prevState.modificados.acoes.autorizadas,
            ...prevState.modificados.acoes.nao_autorizadas,
          ],
        },
      },
    }));
  }

  async function salvarAlteracoes() {
    if (!moduloSelecionado) return;

    if (modo === "permissoes") {
      const modulo = modulos.find(item => item.nome === moduloSelecionado);
      if (!modulo) return;

      setLoading(true);
      try {
        await api.put(
          `/v2/controleAcesso/usuario/${usuarioSelecionado.id}/permissoes/${modulo.id}`,
          {
            permissoes: dadosUsuario.modificados.acoes.autorizadas,
          }
        );

        setDadosUsuario(prevState => ({
          ...prevState,
          originais: {
            ...prevState.modificados,
          },
        }));

        if (username === usuarioSelecionado.username) {
          setReload(prevState => !prevState);
        } else {
          setPermissoesUsuario(dadosUsuario.modificados.acoes.autorizadas);
        }

        enqueueSnackbar("Alterações salvas com sucesso.", {
          variant: "success",
          autoHideDuration: 3000,
        });
      } catch (e) {
        enqueueSnackbar(
          "Não foi possível salvar suas alterações. Recarregue a página e tente novamente.",
          {
            variant: "error",
            autoHideDuration: 3000,
          }
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    checarPermissaoComAcao("CA003", async () => {
      setLoading(true);
      try {
        await api.put(`/v2/controleAcesso/usuario/${usuarioSelecionado.id}/papel`, {
          id_papel: dadosUsuario.modificados.papel,
        });

        enqueueSnackbar("Alterações salvas com sucesso.", {
          variant: "success",
          autoHideDuration: 3000,
        });

        if (username === usuarioSelecionado.username) {
          sobreescreverPermissoes(permissoesPapelSelecionado);
        } else {
          setPermissoesUsuario(permissoesPapelSelecionado);
        }

        setDadosUsuario(prevState => ({
          ...prevState,
          originais: {
            ...prevState.modificados,
          },
        }));
        setUsuarioSelecionado({
          ...usuarioSelecionado,
          id_papel: dadosUsuario.modificados.papel,
        });
      } catch (e) {
        enqueueSnackbar("Algo deu errado. Tente novamente.", {
          variant: "error",
          autoHideDuration: 3000,
        });
      } finally {
        setLoading(false);
      }
    });
  }

  function alternarModo() {
    const proximoModo = modo === "permissoes" ? "cargos" : "permissoes";

    if (existemAlteracoesDetalhadas) {
      mostrarDialogAlteracoesNaoSalvas(() => setModo(proximoModo));
      return;
    }

    setModo(proximoModo);
  }

  function alterarModulo(event) {
    const novoModulo = event.target.value;

    if (existemAlteracoesDetalhadas) {
      mostrarDialogAlteracoesNaoSalvas(() => setModuloSelecionado(novoModulo));
      return;
    }

    setModuloSelecionado(novoModulo);
  }

  function alterarModoVisualizacao(proximoModo) {
    if (modoVisualizacao === proximoModo) return;

    if (existemAlteracoesAtivas) {
      mostrarDialogAlteracoesNaoSalvas(() => setModoVisualizacao(proximoModo));
      return;
    }

    setModoVisualizacao(proximoModo);
  }

  const acoesNaoAutorizadas = dadosUsuario.modificados.acoes.nao_autorizadas;
  const acoesAutorizadas = dadosUsuario.modificados.acoes.autorizadas;

  return (
    <main className="controle-acesso-wrapper">
      <CustomDialog
        open={dialog.open}
        title={dialog.title}
        text={dialog.text}
        continuar={dialog.continuar}
        voltar={dialog.voltar}
      />

      <div className="painel-header">
        <div className="painel-view-tabs">
          <Button
            type="button"
            variant={modoVisualizacao === "geral" ? "contained" : "outlined"}
            onClick={() => alterarModoVisualizacao("geral")}
          >
            Visão geral
          </Button>
          <Button
            type="button"
            variant={modoVisualizacao === "detalhado" ? "contained" : "outlined"}
            onClick={() => alterarModoVisualizacao("detalhado")}
          >
            Detalhado
          </Button>
        </div>

        {loading && (
          <span className="painel-loading">
            <CircularProgress size={18} />
            Carregando
          </span>
        )}
      </div>

      {modoVisualizacao === "geral" && (
        <VisaoGeralPermissoes
          usuarioSelecionado={usuarioSelecionado}
          permissoesUsuario={permissoesUsuario}
          setPermissoesUsuario={setPermissoesUsuario}
          setLoading={setLoading}
          enqueueSnackbar={enqueueSnackbar}
          onDirtyChange={setExistemAlteracoesMacro}
          onSaved={() => {
            if (username === usuarioSelecionado.username) {
              setReload(prevState => !prevState);
            }
          }}
        />
      )}

      {modoVisualizacao === "detalhado" && (
        <>
          <Paper className="controle-acesso-topo">
            <TextField
              className="usuario-selecionado"
              disabled
              label="Usuário selecionado"
              size="small"
              value={usuarioSelecionado.username || ""}
            />
            <FormControl className="modulo-select" size="small">
              <InputLabel id="modulo-label">Módulo</InputLabel>
              <Select
                labelId="modulo-label"
                label="Módulo"
                value={moduloSelecionado}
                onChange={alterarModulo}
              >
                {modulos.map(modulo => (
                  <MenuItem value={modulo.nome} key={`${modulo.nome}-${modulo.id}`}>
                    {modulo.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Paper>

          {!moduloSelecionado ? (
            <section className="painel-module-empty">
              Selecione um módulo para visualizar e gerenciar as permissões do usuário.
            </section>
          ) : (
            <section className="controle-acesso-baixo">
              <div className="controle-acesso-baixo-conteudo">
                <div className="painel-secao">
                  <h2>{modo === "permissoes" ? "Ações não autorizadas" : "Cargos"}</h2>
                  <Paper className="painel-secao-card">
                    <div className="card-conteudo">
                      {modo === "permissoes" &&
                        (acoesNaoAutorizadas.length > 0 ? (
                          acoesNaoAutorizadas.map(acao => (
                            <PermissaoAcesso
                              interactive
                              codigo={acao.codigo}
                              nome={acao.nome}
                              key={`acao-${acao.id}`}
                              inserirPreMudanca={() =>
                                setPreMudanca(prevState => ({
                                  ...prevState,
                                  autorizadas: [...prevState.autorizadas, acao],
                                }))
                              }
                              removerPreMudanca={() =>
                                setPreMudanca(prevState => ({
                                  ...prevState,
                                  autorizadas: removerPreMudanca(prevState.autorizadas, acao),
                                }))
                              }
                            />
                          ))
                        ) : (
                          <EstadoVazio>Nenhuma ação disponível.</EstadoVazio>
                        ))}

                      {modo === "cargos" &&
                        (papeisModuloSelecionado.length > 0 ? (
                          papeisModuloSelecionado.map(papel => (
                            <PermissaoPapel
                              key={`papel-${papel.id}`}
                              nome={papel.nome}
                              descricao={papel.descricao}
                              selected={papel.id === dadosUsuario.modificados.papel}
                              onClick={() => {
                                setDadosUsuario(prevState => ({
                                  ...prevState,
                                  modificados: {
                                    ...prevState.modificados,
                                    papel: papel.id,
                                  },
                                }));
                              }}
                            />
                          ))
                        ) : (
                          <EstadoVazio>Nenhum cargo disponível.</EstadoVazio>
                        ))}
                    </div>

                    <button className="card-footer" type="button" onClick={alternarModo}>
                      {modo === "permissoes" ? (
                        <PersonIcon fontSize="large" />
                      ) : (
                        <CheckBoxIcon fontSize="large" />
                      )}
                      <span>
                        {modo === "permissoes"
                          ? "Utilizar cargos pré-definidos"
                          : "Selecionar ações individuais"}
                      </span>
                    </button>
                  </Paper>
                </div>

                <div className="painel-botoes">
                  {modo === "permissoes" && (
                    <>
                      <Button
                        className="painel-botao"
                        variant="contained"
                        onClick={moverTodosParaDireita}
                        aria-label="Autorizar todas as ações"
                      >
                        <KeyboardDoubleArrowRightIcon />
                      </Button>
                      <Button
                        className="painel-botao"
                        variant="contained"
                        onClick={moverParaDireita}
                        aria-label="Autorizar ações selecionadas"
                      >
                        <KeyboardArrowRightIcon />
                      </Button>
                      <Button
                        className="painel-botao"
                        variant="contained"
                        onClick={moverParaEsquerda}
                        aria-label="Remover ações selecionadas"
                      >
                        <KeyboardArrowLeftIcon />
                      </Button>
                      <Button
                        className="painel-botao"
                        variant="contained"
                        onClick={moverTodosParaEsquerda}
                        aria-label="Remover todas as ações"
                      >
                        <KeyboardDoubleArrowLeftIcon />
                      </Button>
                    </>
                  )}
                </div>

                <div className="painel-secao">
                  <h2>Ações autorizadas</h2>
                  <Paper className="painel-secao-card">
                    <div className="card-conteudo">
                      {modo === "permissoes" &&
                        (acoesAutorizadas.length > 0 ? (
                          acoesAutorizadas.map(acao => (
                            <PermissaoAcesso
                              codigo={acao.codigo}
                              nome={acao.nome}
                              key={`acao-${acao.id}`}
                              interactive
                              inserirPreMudanca={() =>
                                setPreMudanca(prevState => ({
                                  ...prevState,
                                  nao_autorizadas: [...prevState.nao_autorizadas, acao],
                                }))
                              }
                              removerPreMudanca={() =>
                                setPreMudanca(prevState => ({
                                  ...prevState,
                                  nao_autorizadas: removerPreMudanca(
                                    prevState.nao_autorizadas,
                                    acao
                                  ),
                                }))
                              }
                            />
                          ))
                        ) : (
                          <EstadoVazio>Nenhuma ação autorizada.</EstadoVazio>
                        ))}

                      {modo === "cargos" &&
                        (permissoesPapelSelecionado.length > 0 ? (
                          permissoesPapelSelecionado.map(permissao => (
                            <PermissaoAcesso
                              codigo={permissao.codigo}
                              nome={permissao.nome}
                              key={`acao-${permissao.id}`}
                            />
                          ))
                        ) : (
                          <EstadoVazio>Nenhuma ação autorizada para o cargo.</EstadoVazio>
                        ))}
                    </div>
                  </Paper>
                </div>
              </div>

              <div className="painel-buttons-wrapper">
                <Button
                  onClick={() => {
                    setDadosUsuario(prevState => ({
                      ...prevState,
                      modificados: {
                        ...prevState.originais,
                      },
                    }));
                    setPreMudanca({ autorizadas: [], nao_autorizadas: [] });
                    setPermissoesPapelSelecionado([]);
                  }}
                  className={clsx({
                    disabled: !existemAlteracoesDetalhadas,
                    secondary: true,
                  })}
                  disabled={!existemAlteracoesDetalhadas}
                >
                  Cancelar alterações
                </Button>
                <Button
                  onClick={salvarAlteracoes}
                  className={clsx({ disabled: !existemAlteracoesDetalhadas })}
                  disabled={!existemAlteracoesDetalhadas}
                >
                  Salvar alterações
                </Button>
              </div>
            </section>
          )}
        </>
      )}
    </main>
  );
}
